import { FileText, FileType, FileSpreadsheet, Image, Presentation } from 'lucide-react'
import { isImageDocumentExtension } from '@hub/contracts'

/**
 * Returns the appropriate Lucide icon component based on a document's source file extension.
 * Editor-created documents (no sourceFilePath) default to FileText.
 */
export function getFileIcon(sourceFilePath: string | null | undefined) {
  if (!sourceFilePath) return FileText
  const ext = sourceFilePath.split('.').pop()?.toLowerCase()
  if (ext === 'pdf') return FileType
  if (ext === 'doc' || ext === 'docx') return FileText
  if (ext === 'xls' || ext === 'xlsx') return FileSpreadsheet
  if (ext === 'ppt' || ext === 'pptx') return Presentation
  if (ext && isImageDocumentExtension(`.${ext}`)) return Image
  return FileText
}

const EXT_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
}

/**
 * Returns the MIME type for a document based on its source file extension.
 * Editor-created documents (no sourceFilePath) default to text/markdown.
 */
export function getDocumentMime(sourceFilePath: string | null | undefined): string {
  if (!sourceFilePath) return 'text/markdown'
  const ext = sourceFilePath.split('.').pop()?.toLowerCase() ?? ''
  return EXT_MIME[ext] ?? 'application/octet-stream'
}

/**
 * Maps a source file extension to a human-readable source type.
 */
export type DocumentSourceType = 'editor' | 'pdf' | 'word' | 'excel' | 'powerpoint'

export function getDocumentSourceType(
  sourceFilePath: string | null | undefined
): DocumentSourceType {
  if (!sourceFilePath) return 'editor'
  const ext = sourceFilePath.split('.').pop()?.toLowerCase()
  if (ext === 'pdf') return 'pdf'
  if (ext === 'doc' || ext === 'docx') return 'word'
  if (ext === 'xls' || ext === 'xlsx') return 'excel'
  if (ext === 'ppt' || ext === 'pptx') return 'powerpoint'
  return 'editor'
}

/**
 * Returns the file extension (with leading dot) from a stored
 * `source_file_path` like `_originals/doc_01ks….pdf`. Returns '' when
 * there's no extension or no path.
 */
function extFromSourcePath(sourceFilePath: string | null | undefined): string {
  if (!sourceFilePath) return ''
  const lastSegment = sourceFilePath.split('/').pop() ?? ''
  const dot = lastSegment.lastIndexOf('.')
  return dot >= 0 ? lastSegment.slice(dot) : ''
}

/**
 * Returns the display name for a document (shown in UI).
 * - Uploaded/converted docs: `<title><ext>` — the user's title plus the
 *   extension from the stored filename (e.g. "annual-report.pdf").
 *   Uploads are stored on disk as `${id}${ext}` (no original filename),
 *   so we reconstruct a friendly name from the DB `title` column.
 * - Editor-created docs: just the title (no extension).
 */
export function resolveDocumentDisplayName(doc: {
  title: string
  sourceFilePath?: string | null
}): string {
  const ext = extFromSourcePath(doc.sourceFilePath)
  return ext ? `${doc.title}${ext}` : doc.title
}

/**
 * Returns a filename with the correct extension for agent processing.
 * The agent-respond function uses extname(filename) to determine how to
 * process the attachment (markitdown for PDFs/Office, text read for .md).
 */
export function resolveDocumentFilename(doc: {
  title: string
  sourceFilePath?: string | null
}): string {
  const ext = extFromSourcePath(doc.sourceFilePath)
  if (ext) return `${doc.title}${ext}`
  // Editor-created documents → title.md so agent knows it's markdown
  return `${doc.title}.md`
}

/**
 * Builds the workspace URL for serving a document file.
 * - Documents with a sourceFilePath use that path directly.
 * - Editor-created documents fall back to documents/{slug}.md.
 */
export function resolveDocumentUrl(
  projectId: string,
  doc: { slug: string; sourceFilePath?: string | null }
): string {
  if (doc.sourceFilePath) {
    return `/workspaces/${projectId}/${doc.sourceFilePath}`
  }
  return `/workspaces/${projectId}/documents/${doc.slug}.md`
}
